import { useRef, useState, type DragEvent } from 'react';
import type { InvoiceIngestParseItem } from '@stfors/shared-types';
import { ApiError } from '../../api/client';
import { useParseInvoicePdfs } from '../../api/queries/invoice-ingest';
import { IconAction } from '../IconAction';
import { InvoicePdfReview } from './InvoicePdfReview';
import './engagement-ingest.css';
import './invoice-ingest.css';

const MAX_FILES = 10;
const MAX_BYTES_EACH = 15 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

interface Props {
  onApplied: (createdCount: number) => void;
  onCancel?: () => void;
}

/**
 * Drag-and-drop up to 10 invoice PDFs → parse → multi-invoice review → apply.
 */
export function InvoicePdfIngest({ onApplied, onCancel }: Props): JSX.Element {
  const parse = useParseInvoicePdfs();
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<InvoiceIngestParseItem[] | null>(null);
  /** base64 per parse index, for archive on apply */
  const [pdfByIndex, setPdfByIndex] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (list: FileList | File[]): Promise<void> => {
    setError(null);
    const arr = Array.from(list);
    if (arr.length === 0) return;

    const pdfs = arr.filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    if (pdfs.length === 0) {
      setError('Please choose PDF files.');
      return;
    }
    if (pdfs.length > MAX_FILES) {
      setError(`You can import at most ${MAX_FILES} PDFs at a time.`);
      return;
    }
    const tooBig = pdfs.find((f) => f.size > MAX_BYTES_EACH);
    if (tooBig) {
      setError(`"${tooBig.name}" is over 15 MB.`);
      return;
    }

    setFiles(pdfs);
    try {
      const payloads = await Promise.all(
        pdfs.map(async (f) => ({
          filename: f.name,
          content_base64: await fileToBase64(f),
        })),
      );
      const res = await parse.mutateAsync(payloads);
      const byIdx: Record<number, string> = {};
      for (const item of res.items) {
        const src = payloads[item.index];
        if (src) byIdx[item.index] = src.content_base64;
      }
      setPdfByIndex(byIdx);
      setItems(res.items);
    } catch (err) {
      setFiles([]);
      setItems(null);
      setError(
        err instanceof ApiError && err.status === 403
          ? "You don't have permission to import invoices."
          : err instanceof ApiError
            ? err.message
            : 'Could not parse the PDFs. Try again.',
      );
    }
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
  };

  const reset = (): void => {
    setItems(null);
    setFiles([]);
    setPdfByIndex({});
    setError(null);
  };

  if (items) {
    return (
      <InvoicePdfReview
        items={items}
        pdfByIndex={pdfByIndex}
        sourceFiles={files}
        onApplied={(n) => {
          reset();
          onApplied(n);
        }}
        onCancel={onCancel ?? reset}
        onBack={reset}
      />
    );
  }

  return (
    <div className="eli-wrap inv-ingest">
      <div
        className={`eli-dropzone${dragOver ? ' eli-dropzone-over' : ''}${parse.isPending ? ' eli-dropzone-busy' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !parse.isPending && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !parse.isPending) inputRef.current?.click();
        }}
        role="button"
        tabIndex={0}
        aria-label="Drop invoice PDF files"
      >
        {parse.isPending ? (
          <p className="eli-drop-title">Reading {files.length || '…'} invoice PDF(s)…</p>
        ) : (
          <>
            <p className="eli-drop-title">Drop invoice PDFs here</p>
            <p className="eli-drop-meta">
              Up to {MAX_FILES} files · Street Electronic / Target Investigations template · text PDFs only
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p className="inv-ingest-error" role="alert">{error}</p>
      )}
      {onCancel && (
        <div className="eli-actions">
          <IconAction action="cancel" onClick={onCancel} label="Cancel import" />
        </div>
      )}
    </div>
  );
}
